import { reportHandledException } from '@/lib/error-reporting'
/**
 * GrowthBook configuration — the public SDK connection details for A/B tests
 * and feature flags.
 *
 * Mirrors the shape and the fail-closed philosophy of `lib/analytics/config.ts`:
 * IDs are `NEXT_PUBLIC_*` (they ship to the browser by design — the SDK
 * evaluates features locally in the client), read through functions so tests
 * can set `process.env.*` first, and validated so a malformed value disables
 * GrowthBook entirely rather than pointing the SDK somewhere unexpected.
 *
 * When either value is missing or malformed the whole integration becomes a
 * no-op: no payload is fetched, the provider mounts an inert instance, and
 * every experiment falls back to its hard-coded default variant. Dev and
 * preview environments therefore run fine with nothing configured.
 */

/**
 * GrowthBook SDK client keys are prefixed tokens, e.g. `sdk-abc123…`.
 * Requiring the prefix keeps an accidentally-pasted *secret* API key
 * (`secret_…`) — which must never reach the browser — out of the client bundle.
 */
const CLIENT_KEY_PATTERN = /^sdk-[A-Za-z0-9]{6,}$/

/** Only absolute https origins; the SDK fetches the payload from here. */
const isHttpsOrigin = (raw: string): boolean => {
  try {
    return new URL(raw).protocol === 'https:'
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'lib/growthbook/config.ts' })
    return false
  }
}

/** Default GrowthBook Cloud CDN, used when no host is configured explicitly. */
export const GROWTHBOOK_DEFAULT_API_HOST = 'https://cdn.growthbook.io'

/** Payload CDN origin. Falls back to GrowthBook Cloud. */
export const growthbookApiHost = (): string => {
  const value = process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST?.trim() ?? ''
  if (!value) return GROWTHBOOK_DEFAULT_API_HOST
  return isHttpsOrigin(value) ? value.replace(/\/$/, '') : GROWTHBOOK_DEFAULT_API_HOST
}

/** SDK client key, e.g. `sdk-abc123`. Empty string when unset/malformed. */
export const growthbookClientKey = (): string => {
  const value = process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY?.trim() ?? ''
  return CLIENT_KEY_PATTERN.test(value) ? value : ''
}

/** True when the SDK has everything it needs to fetch a payload. */
export const isGrowthBookEnabled = (): boolean => growthbookClientKey().length > 0
